import { uniq } from "lodash";

import type { TagsSchema } from "./types";

/**
 * Ensure tags schema is the updated type. We updated the tags schema type to include
 * descriptions for the tag key and values to add more context for the LLM when generating.
 * Previously, the tags schema was typed Record<string, string[]>, where the value was
 * an array of possible tag values combined with the existing messages tags.
 *
 * XXX: We need to combine with existing message tags because we weren't always updating
 * the tags schema when user add new options in the tags selector when editing message or
 * variant tags outside in the message components. (TODO @jenn-chan: update those
 * components so we don't have to combine with existing message tags.)
 *
 * @param tagsSchema - The tags schema to migrate
 * @param existingTagValues - The existing tag values from messages to combine with the tags schema
 * @returns The migrated tags schema
 */
export const migrateTagsSchema = (
  tagsSchema: Record<string, string[]> | TagsSchema | null | undefined,
  existingTagValues: Record<string, string[]>,
): TagsSchema => {
  const tagKeys = uniq(
    Object.keys(tagsSchema ?? {}).concat(Object.keys(existingTagValues)),
  );

  const migratedTagsSchema: TagsSchema = {};
  for (const key of tagKeys) {
    const schema = tagsSchema?.[key] ?? [];
    const existingValues: string[] = existingTagValues[key] ?? [];

    // If old schema type, combine all the tag values into a unique list and map to new type
    if (Array.isArray(schema)) {
      const allTagValues = uniq(schema.concat(existingValues)).filter(Boolean);
      migratedTagsSchema[key] = {
        tag_values: allTagValues.map((value) => ({ value })),
      };
      // If new schema type, combine any existing tag values that doesn't exist in the schema
    } else if (schema && typeof schema === "object" && "tag_values" in schema) {
      const valuesToAdd = existingValues.filter(
        (value) => value && !schema.tag_values.find((v) => v.value === value),
      );

      migratedTagsSchema[key] = {
        ...schema,
        tag_values: schema.tag_values.concat(
          valuesToAdd.map((value) => ({ value })),
        ),
      };
    }
    // Fallback to an empty list of tag values
    else {
      migratedTagsSchema[key] = {
        tag_values: [],
      };
    }
  }

  return migratedTagsSchema;
};
